import { type CartViewEntryDTO, type LineItemDTO } from "~/features/cart/cart/dto/CartViewDTO";
import * as WeightUtils from "~/utils/WeightUtils";
import { type QuoteCartViewDTO } from "~/features/cart/cart/dto/QuoteCartViewDTO";
import { type CartEntry, type LineItem } from "~/features/cart/cart/models/CartData";
import { transformLinear, transformProduct } from "~/features/productDetails/transformers/ProductDataTransformer";
import { getNextUID } from "~/common/utils/uid";
import { type TransformedCartData } from "~/features/cart/cart/transformers/CartViewDataTransformer";
import { toMapFn } from "~/common/utils/reducers";
import { type NetPriceData } from "~/domains/pricing/types/PriceData";
import { isSubscription } from "~/utils/productUtils";
import { isSurchargeIncludedInPriceValue } from "~/utils/features";
import type { ProductData } from "~/domains/product/types/ProductData";
import { isQuotePackEntryValid } from "~/features/cart/cart/redux/reducers/lineItemValidations";
import { INVALID_ENTRY_ID } from "~/features/quickEntry/constants/constants";

export function transform(dto: QuoteCartViewDTO): TransformedCartData {
  const entries = dto.entries || [];

  const accountNumber = (dto.quoteAccount && dto.quoteAccount.displayUid) || "";

  const data = entries.map((entry) => {
    const product = entry.productLinearInfo
      ? transformLinear(entry.product, entry.productLinearInfo)
      : transformProduct(entry.product);
    const cartEntryPk = entry.pk;
    const { cartEntry, packageEntries } = transformEntry(entry, cartEntryPk, product);
    return {
      product,
      cartEntry,
      packageEntries,
    };
  });
  const productByCode = toMapFn(
    data,
    (data) => data.product.code,
    (data) => data.product,
  );
  const cartEntries = data.map((item) => item.cartEntry);
  const packageEntries = data.map((item) => item.packageEntries).flat();

  return {
    accountNumber,
    cartEntries: cartEntries.map((entry) => entry.id),
    cartEntriesById: toMapFn(cartEntries, (entry) => entry.id),
    packEntriesById: toMapFn(packageEntries, (entry) => entry.id),
    productByCode,
    dstError: dto.dstError,
    comment: "",
  };
}

function transformEntry(
  dto: CartViewEntryDTO,
  cartEntryPk: number,
  product: ProductData,
): { cartEntry: CartEntry; packageEntries: LineItem[] } {
  const cartEntryId = getNextUID();
  const cartEntryNumber = dto.entryNumber;
  const entries = (dto.packageEntries || []).map((entry) =>
    transformPackageEntry(cartEntryId, cartEntryNumber, cartEntryPk, entry, product),
  );

  return {
    cartEntry: {
      pk: -1,
      productCode: product.code,
      packEntryIds: entries.map((entry) => entry.id),
      entryNumber: cartEntryNumber,
      id: cartEntryId,
    },
    packageEntries: entries,
  };
}

function transformPackageEntry(
  cartEntryId: number,
  cartEntryNumber: number,
  cartEntryPk: number,
  dto: LineItemDTO,
  product: ProductData,
): LineItem {
  const deliveryDate = dto.selectedDeliveryDate ? new Date(dto.selectedDeliveryDate) : new Date();

  const weight = product.weight ?? WeightUtils.zero();

  const offerPrice = dto.offerPrice || undefined;
  const targetPrice = dto.targetPrice || undefined;

  let netPrice: NetPriceData | undefined = undefined;
  if (dto.netPrice) {
    const surcharge = dto.surcharge ?? 0;
    const value = dto.netPrice.value;
    const baseValue = isSurchargeIncludedInPriceValue() ? value : value / (1 + surcharge);
    netPrice = {
      value,
      baseValue,
      surcharge,
      currencyIso: dto.netPrice.currencyIso,
      unit: dto.priceUnit ?? 1,
      isSubscription: isSubscription(product),
    };
  }

  const item: LineItem = {
    pk: dto.pk,
    cartEntryPk: cartEntryPk,
    id: getNextUID(),
    cartEntryId,
    cartEntryNumber,
    productCode: product.code,
    comment: dto.comment || "",
    deliveryDate: deliveryDate,
    manuallySetRDD: false,
    packSize: dto.packageSize,
    packCode: dto.packageCode,
    quantity: dto.quantity,
    weight: weight,
    netPrice,
    valid: true,
    entryNumber: dto.entryNumber,
    primaryWarehouseName: dto.primaryWarehouseName || "",
    primaryWarehouseCode: dto.primaryWarehouseCode || "",
    internalComment: dto.internalComment || "",
    showComments: false,
    priceOverriden: !!offerPrice,
    offerPrice,
    msp: dto.minimumSellingPrice || undefined,
    rsp: dto.recommendedSellingPrice || undefined,
    targetPrice,
    messages: [],
    contractNumber: "",
    contractLine: "",
    disableEdit: false,
    source: dto.source,
    priceReference: dto.priceReference ?? "",

    edd: null,
    isInStock: null,

    // Quote fields
    quoteLine: "",
    quoteNumber: "",
    minimumQty: 1,
    isBundled: false,
    original: true,
    quoteExpiryDate: null,
    estimatedShippingDate: null,

    // UI Fields
    isPackagingSelectionOpen: false,

    // Salesmen are never restricted
    restrictedPackage: false,

    optionalPartIds: [],

    customerOrderNumber: "",
    countryOfOrigin: dto.originCountry ?? "",
    tariffSurcharge: false,
  };

  item.valid = isQuotePackEntryValid(item);
  item.cartEntryPk = cartEntryPk !== null ? cartEntryPk : INVALID_ENTRY_ID;
  return item;
}
